// Ana sayfanın sözleşmesi olan ek kartları. Her kanca ya bir kart ya da null
// döner: null, çağıranın bu veriyi okuma yetkisi olmadığı (403) veya modülün bu
// kiracıda kapalı olduğu anlamına gelir. Kart hata kutusu çizmez, sessizce
// çekilir; yetki sınırı yine sunucudadır, istemci kapısı yalnız boş isteği önler.

import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Badge, Card, CardBody, CardHeader, Empty, ErrorState, Loading } from "@novemai/components";

import { platformGraph } from "@/api/platform-graph";
import { EnvelopeError, errorMessage, unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";
import type { paths } from "@/api/schema.generated";
import { useScopeTier } from "@/api/use-scope-tier";
import { useTimeZone } from "@/api/use-time-zone";
import { useVisibleMenu } from "@/components/use-visible-menu";
import { auditActionTerm, auditEntityTerm } from "@/features/admin-settings/vocabulary";
import { fetchAuditEventsPage } from "@/features/admin-settings/audit-api";
import { useAnnouncements } from "@/features/announcements/api";
import { fetchHolidays, type HolidayRow } from "@/features/holidays/api";
import { fetchLeaveBalancesPage } from "@/features/leave/leave-api";
import { formatLeaveSpan } from "@/features/leave/leave-days";
import { fetchPayRunsPage } from "@/features/payroll/payroll-api";
import { formatRunPeriod } from "@/features/payroll/period";
import { capabilityMatches, useEffectivePermissions } from "@/features/roles-permissions/use-effective-permissions";
import { fetchTasksPage } from "@/features/tasks/api";
import { formatDate } from "@/lib/date-range";
import type { HomeWidget } from "./home-dashboard";

const LIST_LIMIT = 5;
const CLOSED_RUN_STATES: ReadonlySet<string> = new Set(["finalized", "cancelled"]);
// Sunucu filtresi zorunlu: liste created_at ASC ve 100 satır sayfalı döner, süzmeyi
// istemcide yapmak 100 eski kapalı görevi olan çalışana "açık görev yok" gösteriyordu.
const OPEN_TASK_STATUSES = ["in_progress", "open"] as const;

/** Yetkisiz okuma ve kapalı modül kartı gizler; diğer hatalar kartta görünür. */
export function hidesWidget(error: unknown): boolean {
  return error instanceof EnvelopeError && (error.httpStatus === 403 || error.hasCode("payroll_tr.disabled"));
}

function todayIso(timeZone: string): string {
  // en-CA biçimi YYYY-MM-DD verir; tatil satırları da aynı takvim tarihidir.
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

function formatInstant(iso: string, locale: string, timeZone: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short", timeZone }).format(new Date(iso));
}

function WidgetCard({ title, subtitle, link, children }: {
  readonly title: string;
  readonly subtitle?: string;
  readonly link?: { readonly to: string; readonly label: string };
  readonly children: ReactNode;
}): React.ReactElement {
  return <Card>
    <CardHeader>
      <div className="min-w-0">
        <div className="nai-card-title">{title}</div>
        {subtitle !== undefined && <div className="nai-card-subtitle">{subtitle}</div>}
      </div>
      {link !== undefined && <Link to={link.to} className="shrink-0 font-sans text-[13px] text-[var(--accent)] no-underline hover:underline">{link.label}</Link>}
    </CardHeader>
    {children}
  </Card>;
}

function QueryState({ isLoading, error, empty, children }: {
  readonly isLoading: boolean;
  readonly error: unknown;
  readonly empty: string | null;
  readonly children: ReactNode;
}): React.ReactElement {
  const { t } = useTranslation();
  if (isLoading) return <CardBody><Loading label={t("home_dashboard.widget_loading")} rows={3} /></CardBody>;
  if (error !== null && error !== undefined) return <CardBody><ErrorState title={t("home_dashboard.widget_error")} description={errorMessage(error, t)} /></CardBody>;
  if (empty !== null) return <CardBody><Empty title={empty} /></CardBody>;
  return <>{children}</>;
}

const rowClass = "flex items-center gap-3 border-t border-[var(--border-subtle)] px-[22px] py-3 first:border-t-0 text-[var(--fg-primary)] no-underline hover:bg-[var(--bg-subtle)]";

type ApprovalList = paths["/v1/approvals"]["get"]["responses"][200]["content"]["application/json"]["data"];
type ApprovalsGet = (
  path: "/v1/approvals",
  init: { params: { query: { assigned_to_me: "true"; status: "pending" } } },
) => Promise<EnvelopedFetchResult<ApprovalList>>;

export function useApprovalQueueWidget(): HomeWidget | null {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const scopeTier = useScopeTier();
  const query = useQuery({
    queryKey: ["approvals", "assigned-to-me", "pending", "home"],
    queryFn: () => unwrapEnvelope((platformGraph.GET as unknown as ApprovalsGet)("/v1/approvals", { params: { query: { assigned_to_me: "true", status: "pending" } } })),
  });
  if (hidesWidget(query.error) || scopeTier === undefined) return null;
  const total = query.data?.total ?? 0;
  // Kendi satırına kapsamlı çalışana boş kuyruk gürültüdür; atanmış onayı varsa görür.
  if (scopeTier === "employee_self" && query.isSuccess && total === 0) return null;
  const rows = (query.data?.approvals ?? []).slice(0, LIST_LIMIT);
  const title = t("home_dashboard.approval_queue.title");
  return { id: "approval_queue", label: title, content: <WidgetCard title={title}
    {...(query.isSuccess && { subtitle: t("home_dashboard.approval_queue.subtitle", { count: total }) })}
    link={{ to: "/requests", label: t("home_dashboard.approval_queue.view_all") }}>
    <QueryState isLoading={query.isLoading} error={query.error} empty={rows.length === 0 ? t("home_dashboard.approval_queue.empty") : null}>
      <ul className="m-0 list-none p-0">{rows.map(row => <li key={row.request_id}>
        <Link to={`/approvals/${row.request_id}`} className={rowClass}>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[14px]">{row.summary}</span>
            {row.created_at !== null && <span className="block text-[12px] text-[var(--fg-muted)]">{t("home_dashboard.approval_queue.opened", { date: formatInstant(row.created_at, i18n.language, timeZone) })}</span>}
          </span>
          {row.sla_due_at !== null && <Badge tone={Date.parse(row.sla_due_at) < Date.now() ? "danger" : "warning"}>{t("home_dashboard.approval_queue.due", { date: formatInstant(row.sla_due_at, i18n.language, timeZone) })}</Badge>}
        </Link>
      </li>)}</ul>
    </QueryState>
  </WidgetCard> };
}

export function useLeaveBalanceWidget(): HomeWidget | null {
  const { t, i18n } = useTranslation();
  const query = useQuery({
    queryKey: ["leave", "balances", "mine", "home-summary"],
    queryFn: () => fetchLeaveBalancesPage(null, true),
  });
  if (hidesWidget(query.error)) return null;
  const rows = (query.data?.items ?? []).filter(row => row.draws_balance !== false).slice(0, 4);
  const title = t("profile.balance_section");
  return { id: "leave_balance", label: title, content: <WidgetCard title={title} link={{ to: "/me/leave/balance", label: t("profile.balance_see_all") }}>
    <QueryState isLoading={query.isLoading} error={query.error} empty={rows.length === 0 ? t("profile.balance_empty") : null}>
      <ul className="m-0 list-none p-0">{rows.map(row => {
        const amount = formatLeaveSpan(row.balance_hours, i18n.language, t) ?? t("common.unknown");
        const cap = row.cap_hours === null ? null : formatLeaveSpan(row.cap_hours, i18n.language, t);
        return <li key={row.balance_id} className="flex items-baseline justify-between gap-3 border-t border-[var(--border-subtle)] px-[22px] py-3 first:border-t-0">
          <span className="min-w-0 truncate text-[14px] text-[var(--fg-primary)]">{row.policy_name ?? row.policy_id.slice(0, 8)}</span>
          <span className="shrink-0 text-[13px] font-medium text-[var(--fg-secondary)]">{cap === null ? t("profile.balance_hours", { amount }) : t("profile.balance_hours_of_cap", { amount, cap })}</span>
        </li>;
      })}</ul>
    </QueryState>
  </WidgetCard> };
}

export function useMyTasksWidget(): HomeWidget | null {
  const { t, i18n } = useTranslation();
  const query = useQuery({
    queryKey: ["tasks", "mine", "home", ...OPEN_TASK_STATUSES],
    queryFn: async () => (await Promise.all(OPEN_TASK_STATUSES.map(status => fetchTasksPage(null, { mine: true, status })))).flatMap(page => page.items),
  });
  if (hidesWidget(query.error)) return null;
  const rows = (query.data ?? []).slice(0, LIST_LIMIT);
  const title = t("home_dashboard.my_tasks.title");
  return { id: "my_tasks", label: title, content: <WidgetCard title={title} link={{ to: "/me/tasks", label: t("home_dashboard.my_tasks.view_all") }}>
    <QueryState isLoading={query.isLoading} error={query.error} empty={rows.length === 0 ? t("home_dashboard.my_tasks.empty") : null}>
      <ul className="m-0 list-none p-0">{rows.map(row => <li key={row.task_id}>
        <Link to={row.link?.startsWith("/") === true && !row.link.startsWith("//") ? row.link : "/me/tasks"} className={rowClass}>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[14px]">{row.title}</span>
            {row.due_at !== null && <span className="block text-[12px] text-[var(--fg-muted)]">{t("home_dashboard.my_tasks.due", { date: formatDate(i18n.language, row.due_at.slice(0, 10)) })}</span>}
          </span>
          <Badge tone={row.status === "in_progress" ? "info" : "warning"}>{t(`tasks.status.${row.status}`)}</Badge>
        </Link>
      </li>)}</ul>
    </QueryState>
  </WidgetCard> };
}

export function useAnnouncementsWidget(): HomeWidget | null {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const query = useAnnouncements(true);
  if (hidesWidget(query.error)) return null;
  const rows = query.items.slice(0, 4);
  const title = t("home_dashboard.announcements.title");
  return { id: "announcements", label: title, content: <WidgetCard title={title} link={{ to: "/me/announcements", label: t("home_dashboard.announcements.view_all") }}>
    <QueryState isLoading={query.isLoading} error={query.error} empty={rows.length === 0 ? t("home_dashboard.announcements.empty") : null}>
      <ul className="m-0 list-none p-0">{rows.map(row => {
        const published = row.actual_published_at ?? row.publish_at;
        return <li key={row.announcement_id}>
          <Link to={`/me/announcements/${row.announcement_id}`} className={rowClass}>
            <span aria-hidden="true" className="w-[3px] self-stretch rounded-full bg-[var(--accent)]" />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-serif text-[15px]">{row.title}</span>
              <span className="line-clamp-2 block text-[12.5px] text-[var(--fg-secondary)]">{row.body}</span>
              {published !== null && <span className="block text-[11.5px] text-[var(--fg-muted)]">{formatInstant(published, i18n.language, timeZone)}</span>}
            </span>
            {row.acknowledgement_required && row.acknowledged_at === null && <Badge tone="warning">{t("home_dashboard.announcements.ack_required")}</Badge>}
          </Link>
        </li>;
      })}</ul>
    </QueryState>
  </WidgetCard> };
}

export function useNextPayrollWidget(): HomeWidget | null {
  const { t, i18n } = useTranslation();
  const menu = useVisibleMenu();
  const enabled = menu.visibleKeys.includes("payroll");
  const query = useQuery({ queryKey: ["payroll", "runs", "home-next"], queryFn: () => fetchPayRunsPage(null), enabled });
  if (!enabled || hidesWidget(query.error)) return null;
  const next = query.data?.items.find(run => !CLOSED_RUN_STATES.has(run.state));
  const title = t("home_dashboard.next_payroll.title");
  return { id: "next_payroll", label: title, content: <WidgetCard title={title} link={{ to: "/payroll", label: t("home_dashboard.next_payroll.view_all") }}>
    <QueryState isLoading={query.isLoading} error={query.error} empty={next === undefined ? t("home_dashboard.next_payroll.empty") : null}>
      {next !== undefined && <CardBody>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-serif text-[22px] text-[var(--fg-primary)]">{formatRunPeriod(i18n.language, next)}</span>
          <Badge tone={next.state === "awaiting_approval" || next.state === "draft" ? "warning" : "info"}>{t(`payroll.admin.state.${next.state}`)}</Badge>
        </div>
        <p className="mt-1 text-[13px] text-[var(--fg-secondary)]">{t("home_dashboard.next_payroll.statements", { count: next.statement_count })}</p>
        <Link to={`/payroll/${next.pay_run_id}`} className="mt-3 inline-block text-[13px] font-semibold text-[var(--accent)] no-underline hover:underline">{t("home_dashboard.next_payroll.review")}</Link>
      </CardBody>}
    </QueryState>
  </WidgetCard> };
}

export function useUpcomingHolidaysWidget(): HomeWidget | null {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const { capabilities, isLoading: permissionsLoading } = useEffectivePermissions();
  const enabled = !permissionsLoading && capabilityMatches(capabilities, "settings.read");
  const today = todayIso(timeZone);
  const year = Number(today.slice(0, 4));
  // Uç tek yıl döndürür; yıl sonunda sıradaki tatiller ertesi yıldadır.
  const query = useQuery({
    queryKey: ["holidays", "home-upcoming", year],
    queryFn: async (): Promise<HolidayRow[]> => {
      const [current, following] = await Promise.all([fetchHolidays(year), fetchHolidays(year + 1)]);
      return [...current.items, ...following.items];
    },
    enabled,
  });
  if (!enabled || hidesWidget(query.error)) return null;
  const rows = (query.data ?? []).filter(row => row.date >= today).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 4);
  const title = t("home_dashboard.holidays.title");
  const day = new Intl.DateTimeFormat(i18n.language, { day: "2-digit", timeZone: "UTC" });
  const month = new Intl.DateTimeFormat(i18n.language, { month: "short", timeZone: "UTC" });
  return { id: "upcoming_holidays", label: title, content: <WidgetCard title={title} subtitle={t("home_dashboard.holidays.subtitle")}>
    <QueryState isLoading={query.isLoading} error={query.error} empty={rows.length === 0 ? t("home_dashboard.holidays.empty") : null}>
      <ul className="m-0 list-none py-2 pl-0">{rows.map(row => <li key={row.holiday_id} className="flex items-center gap-3.5 px-[22px] py-2.5">
        <span aria-hidden="true" className="flex h-12 w-11 shrink-0 flex-col items-center justify-center rounded-[10px] border border-[var(--border-subtle)] bg-[var(--bg-subtle)]">
          <span className="font-serif text-[18px] leading-none text-[var(--fg-primary)]">{day.format(new Date(row.date))}</span>
          <span className="mt-0.5 text-[10px] uppercase text-[var(--fg-muted)]">{month.format(new Date(row.date))}</span>
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-serif text-[14.5px] text-[var(--fg-primary)]">{row.name}</span>
          <span className="sr-only">{formatDate(i18n.language, row.date)}</span>
          <span className="block text-[11.5px] text-[var(--fg-muted)]">{t(`holidays.kind_${row.kind}`)}</span>
        </span>
      </li>)}</ul>
    </QueryState>
  </WidgetCard> };
}

export function useRecentActivityWidget(): HomeWidget | null {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const { capabilities, isLoading: permissionsLoading } = useEffectivePermissions();
  const enabled = !permissionsLoading && capabilityMatches(capabilities, "settings.read");
  const query = useQuery({ queryKey: ["audit-events", "home-recent"], queryFn: () => fetchAuditEventsPage({}, null, LIST_LIMIT), enabled });
  if (!enabled || hidesWidget(query.error)) return null;
  const rows = query.data?.items ?? [];
  const title = t("home_dashboard.activity.title");
  return { id: "recent_activity", label: title, content: <WidgetCard title={title} link={{ to: "/settings/audit-log", label: t("home_dashboard.activity.view_all") }}>
    <QueryState isLoading={query.isLoading} error={query.error} empty={rows.length === 0 ? t("home_dashboard.activity.empty") : null}>
      <ul className="m-0 list-none p-0">{rows.map(row => <li key={row.audit_event_id} className="flex items-center gap-3 border-t border-[var(--border-subtle)] px-[22px] py-3 first:border-t-0">
        <span className="min-w-0 flex-1 text-[14px] text-[var(--fg-secondary)]">
          <strong className="mr-1.5 font-semibold text-[var(--fg-primary)]">{row.actor_display_name ?? row.actor_id_short ?? t("home_dashboard.activity.unknown_actor")}</strong>
          {t("home_dashboard.activity.line", { action: auditActionTerm(t, row.action).label, target: row.entity_display_name ?? auditEntityTerm(t, row.entity_kind).label })}
        </span>
        <span className="shrink-0 text-[11.5px] text-[var(--fg-muted)]">{formatInstant(row.ts, i18n.language, timeZone)}</span>
      </li>)}</ul>
    </QueryState>
  </WidgetCard> };
}
